import type { ProgramId } from './programs'

export type ProgrammeRegistrationForm = {
  [key in ProgramId]: any
}

// ----------------------------
// COMMON VALUES
// ----------------------------

const years = [
  '1990 or earlier',
  '1991',
  '1992',
  '1993',
  '1994',
  '1995',
  '1996',
  '1997',
  '1998',
  '1999',
  '2000',
  '2001',
  '2002',
  '2003',
  '2004',
  '2005',
  '2006',
  '2007',
  '2008',
  '2009',
  '2010 or later'
]

const dateInMonths = {
  January: 31,
  February: 29,
  March: 31,
  April: 30,
  May: 31,
  June: 30,
  July: 31,
  August: 31,
  September: 30,
  October: 31,
  November: 30,
  December: 31
}

const getDates = (month: keyof typeof dateInMonths) => {
  const total: number = dateInMonths[month]
  const dates: string[] = []
  for (let i = 0; i < total; i++) {
    dates.push(`${i + 1}`)
  }
  return dates
}

const getMonths = () => {
  const months: string[] = []
  for (const month in dateInMonths) {
    months.push(month)
  }
  return months
}

const genders = ['Male', 'Female']

const msianEthnicities = ['Malay', 'Chinese', 'Indian', 'Others']

const maritalStatus = ['Single', 'Married', 'Divorced', 'Widowed']

const msianStates = [
  'Johor',
  'Kedah',
  'Kelantan',
  'Melaka',
  'Negeri Sembilan',
  'Pahang',
  'Penang',
  'Perak',
  'Perlis',
  'Sabah',
  'Sarawak',
  'Selangor',
  'Terengganu',
  'W.P Kuala Lumpur',
  'W.P Labuan',
  'W.P Putrajaya'
]

const countries = [
  'Malaysia',
  'United Kingdom',
  'United States',
  'Australia',
  'New Zealand',
  'Canada',
  'Singapore',
  'Hong Kong',
  'China',
  'Taiwan',
  'South Korea',
  'Japan',
  'Switzerland',
  'France',
  'Germany',
  'Netherlands',
  'Belgium',
  'Sweden',
  'Ireland',
  'Russia',
  'Denmark',
  'Others'
]

const fieldOfStudy = [
  'Accounting, Banking & Finance (including Islamic Finance)',
  'Business Management (including Int. Business & Economics, etc.)',
  'Communication & Media',
  'Computer & Technology',
  'Education, Humanities, Language & Early Childhood (including Teaching Degree)',
  'Engineering, Manufacturing, Aviation and Construction (including Architecture, Oil and Gas)',
  'Islamic Studies, Syariah, Usuluddin & Philosophy',
  'Law & Political Science',
  'Mathematics, Actuarial Sciences & Statistics',
  'Multimedia & Creative Arts (including Performing Arts)',
  'Agriculture, Veterinary, Applied Sciences, Marine, Environmental & Biotech',
  'Healthcare',
  'Services (including Tourism, Culinary Arts & Hospitality)',
  'General Programme',
  'Others'
]

const highestQualification = [
  `Diploma`,
  `Degree`,
  `Master's & above`,
  `Professional Qualification eg. ACCA`
]

const qualificationTypes = [
  `BA - Bachelor of Arts`,
  `BAcc - Bachelor of Accounting`,
  `BArch - Bachelor of Architecture`,
  `BBA - Bachelor of Business Administration`,
  `BChem - Bachelor of Chemistry`,
  `BCom - Bachelor of Commerce`,
  `BEd - Bachelor of Education`,
  `BEng - Bachelor of Engineering`,
  `BEng(Tech) - Bachelor of Engineering in Technology`,
  `BFin - Bachelor of Finance`,
  `BFST - Bachelor of Food Science and Technology`,
  `BH - Bachelor of Humanities`,
  `BHSc - Bachelor of Health Science`,
  `BLing - Bachelor of Linguistics`,
  `BMSc - Bachelor of Medical Science`,
  `BMus - Bachelor of Music`,
  `BN - Bachelor of Nursing`,
  `BNatSci - Bachelor in Natural Science`,
  `BPharm - Bachelor of Pharmacy`,
  `BPhil - Bachelor of Philosophy`,
  `BPhys - Bachelor of Physics`,
  `BPhysio - Bachelor of Physiotherapy`,
  `BSc - Bachelor of Science`,
  `BSocSc - Bachelor of Social Sciences`,
  `BTech - Bachelor of Technology`,
  `LLB - Bachelor of Laws`,
  `MBBS - Bachelors of Medicine and Surgery`,
  `BDS - Bachelor of Dental Surgery`,
  `BVMS - Bachelor of Veterinary Medicine`,
  `MAcc - Master in Accountancy`,
  `MArch - Master of Architecture`,
  `MA - Master of Arts`,
  `MBio - Master of Biomedical Sciences, Master of Biological Sciences`,
  `MBus - Master in Business and Management`,
  `MChD - Master in Dental Surgery`,
  `MChem - Master of Chemistry`,
  `MCompSci - Master of Computer Science`,
  `MEarthSci - Master of Earth Science`,
  `MEcon - Master of Economics`,
  `MEng - Master of Engineering`,
  `MFin - Master of Finance`,
  `MGeog - Master of Geography`,
  `MHist - Master of History`,
  `MLaw - Master of Law`,
  `MMath - Master of Mathematics`,
  `MMkt - Master in Marketing`,
  `MNatSc - Master of Natural Science`,
  `MNeuro - Master in Neuroscience`,
  `MNurs - Master of Nursing`,
  `MPharm - Master of Pharmacy`,
  `MPhys - Master of Physics`,
  `MPsych - Master of Psychology`,
  `MSc - Master of Science`,
  `MSoc - Master of Sociology`,
  `MBA - Master of Business Administration`,
  `MEd - Master of Education`,
  `MPhil - Master of Philosophy`,
  `MRes - Master in Research`,
  `MSW - Master of Social Work`,
  `MTeach - Master of Teaching`,
  `Others`
]

const graduationYears = [
  '2026 or later',
  '2025',
  '2024',
  '2023',
  '2022',
  '2021',
  '2020',
  '2019',
  '2018',
  '2017',
  '2016',
  '2015 or earlier'
]

const englishQualificationScores = {
  IELTS: [
    'IELTS 9',
    'IELTS 8.5',
    'IELTS 8',
    'IELTS 7.5',
    'IELTS 7',
    'IELTS 6.5',
    'IELTS 6',
    'IELTS 5.5',
    'IELTS 5',
    'IELTS 4.5',
    'IELTS 4'
  ],
  MUET: [
    'MUET 6.0',
    'MUET 5.5',
    'MUET 5.0',
    'MUET 4.5',
    'MUET 4.0',
    'MUET 3.5',
    'MUET 3.0',
    'MUET 2.5',
    'MUET 2.0',
    'MUET 1.5',
    'MUET 1.0'
  ],
  'TOEFL iBT': [
    'TOEFL iBT 118-120',
    'TOEFL iBT 115-117',
    'TOEFL iBT 110-114',
    'TOEFL iBT 102-109',
    'TOEFL iBT 94-101',
    'TOEFL iBT 79-93',
    'TOEFL iBT 60-78',
    'TOEFL iBT 46-59',
    'TOEFL iBT 35-45',
    'TOEFL iBT 32-34',
    'TOEFL iBT 0-31'
  ],
  CEFR: ['CEFR C2', 'CEFR C1', 'CEFR B2', 'CEFR B1', 'CEFR A2', 'CEFR A1'],
  'IB English': [
    'IB English 7',
    'IB English 6',
    'IB English 5',
    'IB English 4',
    'IB English 3',
    'IB English 2',
    'IB English 1'
  ]
}

const getEnglishQuals = () => {
  const arr: string[] = []
  for (const key in englishQualificationScores) {
    arr.push(key)
  }
  return arr
}

const getEnglishQualScores = (qual: keyof typeof englishQualificationScores) => {
  return englishQualificationScores[qual]
}

const employmentExperiences = ['Part-time', 'Full-time', 'Internship', 'None']

// ----------------------------
// KYDP 2025
// ----------------------------

const kydpFormOptions = {
  pathways: [
    'Data Analytics',
    'Machine Learning',
    'Prompt Engineering',
    'Front-end Development',
    'Back-end Development',
    'Digital Marketing',
    'UX Design',
    'Fintech',
    'Tech Sales'
  ],
  semiconPathways: [
    'Digital Design (IC Design)',
    'Analog Design (IC Design)',
    '2.5 / 3D Packaging (Advanced Packaging)'
  ],
  years: years,
  dateInMonths: dateInMonths,
  getMonths: getMonths,
  getDates: getDates,
  genders: genders,
  ethnicities: msianEthnicities,
  maritalStatus: maritalStatus,
  states: msianStates,
  countries: countries,
  fieldOfStudy: fieldOfStudy,
  highestQualification: highestQualification,
  qualificationTypes: qualificationTypes,
  graduationYears: graduationYears,
  englishQualificationScores: englishQualificationScores,
  getEnglishQuals: getEnglishQuals,
  getEnglishQualScores: getEnglishQualScores,
  employmentExperiences: employmentExperiences,
  leadershipExperiences: [
    'Voluntary service or non-profit organisations',
    'Social activity groups or community sports',
    'Student bodies, clubs or societies',
    'Mentorship programmes',
    'None'
  ]
}

const yp2025FormOptions = {
  courses: [
    {
      title: `Google Cloud Associate Cloud Engineer`,
      id: 'gc-a-ce'
    },
    {
      title: `Google Cloud Professional Cloud Architect`,
      id: 'gc-p-a'
    },
    {
      title: `Google Cloud Professional Data Engineer`,
      id: 'gc-p-de'
    },
    {
      title: `Google Cloud Professional Cloud Security Engineer`,
      id: 'gc-p-se'
    },
    {
      title: `AWS Certified Solutions Architect (Associate)`,
      id: 'aws-csa-a'
    },
    {
      title: `AWS Certified Solutions Architect (Professional)`,
      id: 'aws-csa-p'
    },
    {
      title: `AWS Certified Machine Learning Engineer (Associate)`,
      id: 'aws-ml-a'
    },
    {
      title: `AWS Certified Machine Learning (Specialty)`,
      id: 'aws-ml-s'
    }
  ],
  getDates: getDates,
  getMonths: getMonths,
  years: years,
  genders: genders,
  ethnicities: msianEthnicities,
  maritalStatus: maritalStatus,
  states: msianStates,
  highestQualification: highestQualification,
  qualificationTypes: qualificationTypes,
  fieldOfStudy: fieldOfStudy,
  countries: countries,
  graduationYears: graduationYears,
  getEnglishQuals: getEnglishQuals,
  getEnglishQualScores: getEnglishQualScores,
  techExperience: [
    `Below 1 year`,
    `1 to 3 years`,
    `3 to 5 years`,
    `5 to 7 years`,
    `7 to 9 years`,
    `Above 9 years`
  ],
  cloudExperience: [`Below 1 year`, `1 to 3 years`, `3 to 5 years`, `Above 5 years`]
}

export const programmeRegistrationForms: ProgrammeRegistrationForm = {
  'kyouth-2025': kydpFormOptions,
  'yayasan-peneraju-2025': yp2025FormOptions,
  'kydp-2024': undefined,
  'cap-2023': undefined,
  dtfs: undefined,
  'google-career-2023': undefined,
  'google-career-2025': undefined,
  'ive-train-2023': undefined,
  'kydp-2023': undefined,
  'kydp-2024-b': undefined,
  'moses-2023': undefined,
  'mystep-2022': undefined
}
